import {useQuery} from "@apollo/client";
import classNames from "classnames";
import {useEffect} from "react";
import {useFieldArray, useFormContext} from "react-hook-form";
import {FiAlertTriangle} from "react-icons/fi";

import {Alert} from "@/components/alert/Alert.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {Select} from "@/components/select/Select.tsx";
import {
  GQL_STATUS_PAGE_QUERY,
  GQL_STATUSPAGE_COMPONENTS,
  StatusPageComponentsListResponse,
} from "@/lib/queries/statuspages.ts";
import {Component, ComponentStatusValue, StatusPageResponse} from "@/lib/types/statuspages.ts";
import {IncidentEditFormData} from "@/pages/incidents/components/incident-edit/IncidentForm.types.ts";
import {isNotificationIncident} from "@/pages/incidents/components/incident-edit/IncidentForm.utils.tsx";
import {NO_CHANGE_OPTION} from "@/pages/statuspages/tabs/components/ComponentStatusSelect/ComponentStatusSelect.constants.ts";
import ComponentStatusSelect from "@/pages/statuspages/tabs/components/ComponentStatusSelect/ComponentStatusSelect.tsx";

export interface AffectedComponentsFieldProps {
  statusPageId?: number;
}

export const AffectedComponentsField = ({statusPageId}: AffectedComponentsFieldProps) => {
  const {setValue, watch, register, control, getValues} = useFormContext<IncidentEditFormData>();
  const {
    fields: affectedComponentFields,
    insert,
    remove,
  } = useFieldArray({
    name: "affected_components",
    control,
    keyName: "key",
  });
  const {data: statusPageData} = useQuery<StatusPageResponse>(GQL_STATUS_PAGE_QUERY, {
    variables: {
      id: statusPageId,
    },
    errorPolicy: "ignore",
    fetchPolicy: "cache-first",
  });

  const {data: componentData} = useQuery<StatusPageComponentsListResponse>(GQL_STATUSPAGE_COMPONENTS, {
    variables: {
      statusPageId: statusPageId,
      excludeGroups: true,
    },
    errorPolicy: "ignore",
    fetchPolicy: "cache-first",
  });

  const components: Component[] = componentData?.response.items || [];
  const selectedComponentIds = (getValues("affected_components") || []).map((ac) => ac.id);
  const componentStatuses = statusPageData?.componentStatuses || [];
  const statusOptions = [NO_CHANGE_OPTION, ...componentStatuses.filter((c) => c.is_failure)];
  // the dropdown only offers failure statuses, but a stored row could hold one it does not offer
  const storedStatusOptions = [NO_CHANGE_OPTION, ...componentStatuses];
  const isNotification = isNotificationIncident(watch("updates"));
  const incidentId = watch("id");

  useEffect(() => {
    // Reset affected components in case of switching the SP for a new incident
    if (!incidentId) {
      setValue("affected_components", []);
    }
  }, [incidentId, setValue, watch, statusPageId]);

  if (components.length === 0) {
    return false;
  }
  return (
    <>
      <Divider />
      <div className={"d-flex flex-column gap-3"}>
        <div className={"d-flex flex-column gap-2"}>
          <div className="d-flex flex-row gap-3">
            <FormLabel
              forId="affected_components"
              className="w-100"
              helpText="OPTIONAL: Select the component(s) affected by this incident."
            >
              Components Affected
            </FormLabel>
            {!isNotification && affectedComponentFields.length > 0 && (
              <FormLabel
                forId="affected_components"
                className="w-100"
                helpText="OPTIONAL: Set the status of this component."
              >
                Update
              </FormLabel>
            )}
          </div>
          {isNotification && (
            <Alert variant={"maintenance"} icon={FiAlertTriangle} className={"mb-0"}>
              Notifications do not affect components.
            </Alert>
          )}
          {/* hidden rather than unmounted, so the selections survive switching states while the form is open */}
          <div className={classNames("d-flex flex-column gap-3", {"d-none": isNotification})}>
            {affectedComponentFields.map((field, idx) => (
              <div className="d-flex flex-row gap-3" key={`${field.key}`}>
                <div className="w-100">
                  {!!field.obj_id && <input type="number" hidden {...register(`affected_components.${idx}.obj_id`)} />}
                  <Select
                    isClearable={true}
                    onChange={(c) => {
                      if (!c) {
                        remove(idx);
                        return;
                      } else {
                        setValue(`affected_components.${idx}.id`, c.id);
                      }
                    }}
                    defaultValue={components.find((c) => c.id === field.id)}
                    getOptionLabel={(c) => c.name}
                    getOptionValue={(c) => c.id.toString()}
                    // We need to display either already selected option, or those which are not yet taken
                    options={components.filter(
                      (c) => !selectedComponentIds.includes(c.id) || getValues(`affected_components.${idx}.id`) === c.id
                    )}
                  />
                </div>
                <div className="w-100">
                  <ComponentStatusSelect
                    defaultValue={storedStatusOptions.find((c) => c.value === field.status)}
                    name={`affected_components.${idx}.status`}
                    onChange={(c) => {
                      if (!c) {
                        return;
                      }
                      setValue(`affected_components.${idx}.status`, c.value);
                    }}
                    options={statusOptions}
                    isDisabled={false}
                    className={classNames({"d-none": watch(`affected_components.${idx}.id`) === 0})}
                  />
                </div>
              </div>
            ))}
            {components.length > 0 && selectedComponentIds.length < components.length && (
              <div className="d-flex flex-row gap-3">
                <div className="w-100">
                  <Select
                    isClearable={false}
                    onChange={(c) => {
                      insert(0, {id: c!.id, obj_id: 0, status: ComponentStatusValue.NO_CHANGE});
                    }}
                    getOptionLabel={(c) => c.name}
                    getOptionValue={(c) => c.id.toString()}
                    options={components.filter((c) => !selectedComponentIds.includes(c.id))}
                    key={`affected-component-selector-${selectedComponentIds.length}`}
                  />
                </div>
                <div className="w-100"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
